import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../api/apiClient";
import {
  ArrowLeft,
  Clock,
  Server,
  CheckCircle,
  Loader,
  CreditCard,
  Hash,
} from "lucide-react";
import { motion } from "framer-motion";

export default function MigrationJobStatus() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const intervalRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await apiClient.get(`/job-status/${jobId}`);
      setJob(res);
    } catch (err) {
      console.error("Job status error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (intervalRef.current) return;

    fetchStatus();

    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, 3000);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [jobId]);

  if (loading || !job)
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-text-secondary">
        Loading job status...
      </div>
    );

  const isCompleted = job.job_status === "completed";

  return (
    <div className="min-h-screen bg-bg px-3 sm:px-6 py-8 text-text-primary">

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-muted hover:text-text-primary mb-6 transition"
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1 className="text-2xl sm:text-3xl font-semibold mb-8 text-text-primary text-center">
        Migration Job Status
      </h1>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`max-w-4xl mx-auto bg-white rounded-xl p-6 shadow-sm border
          ${isCompleted ? "border-green-400" : "border-border"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-text-primary">
            <Server size={18} className="text-text-muted" />
            <span className="text-sm font-medium">Migration Job</span>
          </div>

          <span
            className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium
              ${isCompleted
                ? "bg-green-50 border-green-400 text-green-700"
                : "bg-bg-soft border-border text-text-muted"
              }`}
          >
            {isCompleted ? <CheckCircle size={12} /> : <Loader size={12} />}
            {job.job_status}
          </span>
        </div>

        {/* Job Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
          {[
            {
              icon: <Hash size={12} />,
              label: "Migration Type",
              value: job.migration_type,
            },
            {
              label: "Vector Count",
              value: job.vector_count,
            },
            {
              icon: <CreditCard size={12} />,
              label: "Expected Amount",
              value: `$${job.expected_amount / 100}`,
            },
            {
              label: "Payment Status",
              value: job.payment_status,
            },
          ].map(({ icon, label, value }, i) => (
            <div key={i} className="bg-bg-soft border border-border p-3 rounded-lg">
              <p className="text-text-muted text-xs flex items-center gap-1 mb-1">
                {icon} {label}
              </p>
              <p className="text-text-primary font-medium">{value}</p>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="w-full bg-bg-soft border border-border h-2 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${job.progress}%` }}
              transition={{ duration: 0.6 }}
              className={`h-full ${isCompleted ? "bg-green-500" : "bg-accent"}`}
            />
          </div>

          <p className="text-text-muted flex items-center gap-1 mt-2 text-xs">
            <Clock size={12} /> Progress: {job.progress}%
          </p>
        </div>

        {/* Timestamps */}
        <div className="flex flex-wrap gap-6 text-xs sm:text-sm text-text-secondary">
          <p>
            <b className="text-text-primary">Created:</b>{" "}
            {new Date(job.created_at).toLocaleString()}
          </p>
          <p>
            <b className="text-text-primary">Updated:</b>{" "}
            {new Date(job.updated_at).toLocaleString()}
          </p>
        </div>
      </motion.div>
    </div>
  );
}